/**
 * Deciding which CIS supplier a Promo Office supplier name is.
 *
 * The supplier twin of `CustomerLinking.tsx`, in the same shape and words,
 * with two differences that are the point of it. Nothing links a supplier
 * automatically — a wrong link raises a real purchase order to the wrong
 * supplier — so every row here waits on a person. And a link ends rather than
 * being deleted: the purchase orders it routed stay routed, and the record of
 * why has to outlive it, which is why unlinking asks for a reason.
 *
 * The decision is per *name*, not per line. Promo Office spells one supplier
 * several ways, and resolving a spelling clears every quote line, order line
 * and purchase order that names it.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  PromoOfficeCisSupplier,
  PromoOfficeSupplierLink,
  PromoOfficeSupplierMatch,
  PromoOfficeUnlinkedSupplier,
} from "@shared-core/data/adapter";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { extractFieldErrors } from "@shared-core/data/validation";
import { formatShortDateOrDash } from "@shared-core/domain/formatters";
import {
  describeSupplierLinkDecision,
  describeSupplierMatchedSignals,
  describeSupplierOpenWork,
} from "@shared-core/domain/promo-office-matching";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Card } from "../../primitives/card";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { Textarea } from "../../primitives/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../primitives/dialog";
import { ListPagination } from "../../patterns/ListPagination";
import { useCanManageOrders } from "../../../auth/hooks";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Search,
  Unlink,
  X,
} from "lucide-react";

const PAGE_SIZE = 25;

type CandidateFilter = "all" | "suggested" | "none";

const FILTER_TO_QUERY: Record<CandidateFilter, boolean | undefined> = {
  all: undefined,
  suggested: true,
  none: false,
};

/**
 * A refusal bound to one control, or a message for everything else.
 *
 * The backend names the input it refused (`match_id`, `supplier_no`,
 * `reason`), so the message belongs beside that input rather than in a toast
 * that has gone by the time somebody looks for it. Anything not field-bound —
 * a 404, a 503 while the supplier directory is away — is still a toast.
 */
function fieldMessage(err: unknown, field: string): string | null {
  const bound = extractFieldErrors(err).filter((e) => e.field === field);
  return bound.length > 0 ? bound.map((e) => e.message).join(" ") : null;
}

export function UnlinkedSuppliersTab({
  onResolved,
}: {
  /** Lets the page refresh its tab counts once a supplier leaves the list. */
  onResolved?: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const [suppliers, setSuppliers] = useState<
    PromoOfficeUnlinkedSupplier[] | null
  >(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<CandidateFilter>("all");
  const [search, setSearch] = useState("");
  const [reloadTick, setReloadTick] = useState(0);
  // One row open at a time, held as its key rather than an index so it
  // survives a list that reorders under it.
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSuppliers(null);
    setLoadFailed(false);
    dataAdapter
      .listPromoOfficeUnlinkedSuppliers({
        page,
        pageSize: PAGE_SIZE,
        hasCandidates: FILTER_TO_QUERY[filter],
        q: search.trim() || undefined,
      })
      .then((response) => {
        if (cancelled) return;
        setSuppliers(response.items);
        setTotalPages(response.totalPages);
        setTotal(response.total);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, page, filter, search, reloadTick]);

  const reload = useCallback(() => {
    setExpandedKey(null);
    setReloadTick((n) => n + 1);
    onResolved?.();
  }, [onResolved]);

  // Any change to what is being asked for resets to the first page, or the
  // user lands on page 4 of a two-page result and sees an empty list.
  const changeFilter = (next: CandidateFilter) => {
    setFilter(next);
    setPage(1);
    setExpandedKey(null);
  };

  const changeSearch = (next: string) => {
    setSearch(next);
    setPage(1);
    setExpandedKey(null);
  };

  if (loadFailed) {
    return (
      <Card className="p-12 text-center space-y-3">
        <AlertTriangle className="w-8 h-8 mx-auto text-amber-600" />
        <p className="text-sm text-muted-foreground">
          The list of suppliers with no supplier in One couldn&apos;t be loaded.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setReloadTick((n) => n + 1)}
        >
          Try again
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <FilterButton
            active={filter === "all"}
            onClick={() => changeFilter("all")}
          >
            All
          </FilterButton>
          {/* The two jobs the list really contains. Confirming a suggestion is
              quick; a supplier with none means a search by hand, or a supplier
              that does not exist in One yet. Mixed together the quick wins are
              invisible. */}
          <FilterButton
            active={filter === "suggested"}
            onClick={() => changeFilter("suggested")}
          >
            Has a suggestion
          </FilterButton>
          <FilterButton
            active={filter === "none"}
            onClick={() => changeFilter("none")}
          >
            No suggestion
          </FilterButton>
        </div>
        <SearchBox value={search} onChange={changeSearch} />
      </div>

      {suppliers === null ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : suppliers.length === 0 ? (
        <EmptyState filter={filter} searching={search.trim().length > 0} />
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {total} {total === 1 ? "supplier" : "suppliers"} with open work and
            no supplier in One. Busiest first.
          </p>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-6" />
                  <TableHead>Supplier</TableHead>
                  <TableHead>Creditor no.</TableHead>
                  <TableHead>Postcode</TableHead>
                  <TableHead>Open work</TableHead>
                  <TableHead>Suggestion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.map((supplier) => (
                  <UnlinkedSupplierRow
                    key={supplier.nameKey}
                    supplier={supplier}
                    expanded={expandedKey === supplier.nameKey}
                    onToggle={() =>
                      setExpandedKey((current) =>
                        current === supplier.nameKey ? null : supplier.nameKey,
                      )
                    }
                    onResolved={reload}
                    onCandidateDismissed={() => setReloadTick((n) => n + 1)}
                  />
                ))}
              </TableBody>
            </Table>
            <ListPagination
              page={page}
              totalPages={totalPages}
              onPageChange={(next) => {
                setExpandedKey(null);
                setPage(next);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      size="sm"
      variant={active ? "default" : "outline"}
      onClick={onClick}
      aria-pressed={active}
    >
      {children}
    </Button>
  );
}

function SearchBox({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);

  // Debounced so every keystroke is not a request, but the field itself stays
  // immediately responsive -- a search box that lags behind typing reads as a
  // broken page. Nothing is announced when nothing changed: the committing
  // handler resets the page and collapses the open row, and on mount the
  // draft already equals the value, so an unguarded timer would close a row
  // somebody opened in the first 300ms.
  useEffect(() => {
    if (draft === value) return;
    const timer = setTimeout(() => onChange(draft), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  return (
    <div className="relative w-full sm:w-72">
      <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Search supplier or creditor no."
        className="pl-8"
        aria-label="Search suppliers with no supplier in One"
      />
    </div>
  );
}

function EmptyState({
  filter,
  searching,
}: {
  filter: CandidateFilter;
  searching: boolean;
}) {
  const message = searching
    ? "No supplier matches that search."
    : filter === "suggested"
      ? "Nothing is waiting on a suggestion being confirmed."
      : filter === "none"
        ? "Every supplier here has at least one suggestion to look at."
        : "Every Promo Office supplier with open work has a supplier in One.";

  return (
    <Card className="p-12 text-center">
      <Check className="w-10 h-10 mx-auto mb-3 text-green-600 dark:text-green-400" />
      <h3 className="mb-1">Nothing to decide</h3>
      <p className="text-muted-foreground text-sm">{message}</p>
    </Card>
  );
}

function UnlinkedSupplierRow({
  supplier,
  expanded,
  onToggle,
  onResolved,
  onCandidateDismissed,
}: {
  supplier: PromoOfficeUnlinkedSupplier;
  expanded: boolean;
  onToggle: () => void;
  onResolved: () => void;
  onCandidateDismissed: () => void;
}) {
  const topCandidate = supplier.candidates[0];
  const extraCandidates = supplier.candidates.length - 1;

  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell className="w-6">
          {expanded ? (
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell className="font-medium">
          <span className="flex flex-wrap items-center gap-2">
            {supplier.promoOfficeSupplierName}
            {/* A linked name back in the list is the one state that is not
                "nobody has decided": somebody did, and Promo Office has since
                given the supplier a different creditor number. */}
            {supplier.hasCreditorConflict && (
              <Badge variant="warning" className="font-normal">
                Creditor number changed
              </Badge>
            )}
          </span>
        </TableCell>
        <TableCell className="font-mono text-sm text-muted-foreground">
          {supplier.creditorNumber ?? "—"}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {supplier.postCode ?? "—"}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {describeSupplierOpenWork(supplier) || "—"}
        </TableCell>
        <TableCell className="text-sm">
          {topCandidate ? (
            <span className="text-muted-foreground">
              {topCandidate.cisSupplierName}
              {extraCandidates > 0 && ` and ${extraCandidates} other`}
              {extraCandidates > 1 && "s"}
            </span>
          ) : supplier.liveLink ? (
            <span className="text-muted-foreground">
              Linked to {supplier.liveLink.supplierNo}
            </span>
          ) : (
            <span className="text-muted-foreground">None</span>
          )}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6} className="bg-muted/30 p-0">
            <div
              className="p-4 whitespace-normal"
              // Stop the row's own expand/collapse firing when someone uses a
              // button or types in the search field inside the panel.
              onClick={(e) => e.stopPropagation()}
            >
              <ResolutionPanel
                supplier={supplier}
                onResolved={onResolved}
                onCandidateDismissed={onCandidateDismissed}
              />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function ResolutionPanel({
  supplier,
  onResolved,
  onCandidateDismissed,
}: {
  supplier: PromoOfficeUnlinkedSupplier;
  onResolved: () => void;
  onCandidateDismissed: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const canDecide = useCanManageOrders();
  const [busyMatchId, setBusyMatchId] = useState<string | null>(null);
  // A refusal the backend bound to a suggestion, kept beside that suggestion.
  const [candidateErrors, setCandidateErrors] = useState<
    Record<string, string>
  >({});
  const [finding, setFinding] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  const openWork = describeSupplierOpenWork(supplier);

  const confirmLink = async (match: PromoOfficeSupplierMatch) => {
    setBusyMatchId(match.id);
    setCandidateErrors((current) => ({ ...current, [match.id]: "" }));
    try {
      await dataAdapter.confirmPromoOfficeSupplierMatch(match.id);
      toast.success(
        `${supplier.promoOfficeSupplierName} linked to ${match.supplierNo} ${match.cisSupplierName}`,
      );
      onResolved();
    } catch (err) {
      const bound = fieldMessage(err, "match_id");
      if (bound) {
        setCandidateErrors((current) => ({ ...current, [match.id]: bound }));
      } else {
        toast.error(mutationErrorMessage(err, "Could not link this supplier"));
      }
      setBusyMatchId(null);
    }
  };

  const dismissMatch = async (match: PromoOfficeSupplierMatch) => {
    setBusyMatchId(match.id);
    setCandidateErrors((current) => ({ ...current, [match.id]: "" }));
    try {
      await dataAdapter.dismissPromoOfficeSupplierMatch(match.id);
      toast.success(`${match.cisSupplierName} rejected as a match`);
      onCandidateDismissed();
    } catch (err) {
      const bound = fieldMessage(err, "match_id");
      if (bound) {
        setCandidateErrors((current) => ({ ...current, [match.id]: bound }));
      } else {
        toast.error(
          mutationErrorMessage(err, "Could not reject this suggestion"),
        );
      }
    } finally {
      setBusyMatchId(null);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <section className="space-y-1">
        <h4 className="text-sm font-medium">In Promo Office</h4>
        <p className="text-sm text-muted-foreground">
          {supplier.promoOfficeSupplierName}
          {supplier.town && ` · ${supplier.town}`}
          {supplier.postCode && ` · ${supplier.postCode}`}
          {supplier.creditorNumber &&
            ` · Creditor no. ${supplier.creditorNumber}`}
        </p>
        {openWork && (
          <p className="text-sm text-muted-foreground">
            {openWork} will go to whichever supplier you decide on here.
          </p>
        )}
      </section>

      {supplier.liveLink && (
        <LiveLinkSection
          supplier={supplier}
          link={supplier.liveLink}
          canDecide={canDecide}
          onUnlink={() => setUnlinking(true)}
        />
      )}

      {supplier.candidates.length > 0 ? (
        <section className="space-y-2">
          <h4 className="text-sm font-medium">Suggested suppliers in One</h4>
          <ul className="space-y-2">
            {supplier.candidates.map((candidate) => (
              <CandidateRow
                key={candidate.id}
                candidate={candidate}
                canDecide={canDecide}
                busy={busyMatchId !== null}
                working={busyMatchId === candidate.id}
                error={candidateErrors[candidate.id] || null}
                onConfirm={() => confirmLink(candidate)}
                onDismiss={() => dismissMatch(candidate)}
              />
            ))}
          </ul>
        </section>
      ) : (
        !supplier.liveLink && (
          <section className="space-y-1">
            <h4 className="text-sm font-medium">No suggestions</h4>
            <p className="text-sm text-muted-foreground">
              Nothing in One matched this supplier&apos;s name, creditor number
              or postcode. Find the right one by hand, or ask purchasing to set
              the supplier up in One first.
            </p>
          </section>
        )
      )}

      {!canDecide && (
        // Said as whose job it is, not as a missing button: the list is open
        // to everyone who can see a quote, the decision is not.
        <p className="text-sm text-muted-foreground">
          Linking a supplier decides where purchase orders go, so it needs
          permission to manage orders.
        </p>
      )}

      {/* Not offered while a link is live: the backend refuses a second link
          until the first has ended, so the only way on is Unlink above. */}
      {canDecide && !supplier.liveLink && (
        <section className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setFinding((open) => !open)}
          >
            <Search className="h-3.5 w-3.5" />
            Find a different supplier in One
          </Button>
        </section>
      )}

      {finding && canDecide && !supplier.liveLink && (
        <FindSupplierPanel supplier={supplier} onLinked={onResolved} />
      )}

      {supplier.liveLink && (
        <UnlinkSupplierDialog
          supplier={supplier}
          link={supplier.liveLink}
          open={unlinking}
          onOpenChange={setUnlinking}
          onUnlinked={onResolved}
        />
      )}
    </div>
  );
}

/**
 * The link that already exists, shown because Promo Office has since named
 * the supplier under a different creditor number. The link still routes work
 * meanwhile; the person decides whether it is still the right supplier.
 */
function LiveLinkSection({
  supplier,
  link,
  canDecide,
  onUnlink,
}: {
  supplier: PromoOfficeUnlinkedSupplier;
  link: PromoOfficeSupplierLink;
  canDecide: boolean;
  onUnlink: () => void;
}) {
  return (
    <section className="rounded-md border border-amber-300 bg-background p-3 space-y-2 dark:border-amber-800">
      <h4 className="text-sm font-medium">Linked in One</h4>
      <p className="text-sm">
        <span className="font-mono">{link.supplierNo}</span>{" "}
        <span className="font-medium">{link.cisSupplierName}</span>
        <span className="text-muted-foreground">
          {" "}
          · {describeSupplierLinkDecision(link.decidedBy)},{" "}
          {formatShortDateOrDash(link.decidedAt)}
        </span>
      </p>
      {/* Both creditor numbers on one line: "the creditor changed" is only
          actionable next to the two values it is talking about. */}
      <p className="text-sm text-amber-700 dark:text-amber-400">
        Promo Office now gives this supplier creditor no.{" "}
        {supplier.creditorNumber ?? "none"}; the link was made under{" "}
        {link.creditorNumber ?? "none"}. If it is still the same supplier, leave
        the link as it is. If not, unlink it and link the name again.
      </p>
      {canDecide && (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={onUnlink}>
            <Unlink className="h-3.5 w-3.5" />
            Unlink
          </Button>
          <span className="text-xs text-muted-foreground">
            Purchase orders already matched stay matched. New work naming{" "}
            {supplier.promoOfficeSupplierName} waits until it is linked again.
          </span>
        </div>
      )}
    </section>
  );
}

function CandidateRow({
  candidate,
  canDecide,
  busy,
  working,
  error,
  onConfirm,
  onDismiss,
}: {
  candidate: PromoOfficeSupplierMatch;
  canDecide: boolean;
  busy: boolean;
  working: boolean;
  error: string | null;
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  const errorId = useId();
  const postcodeAgreed = candidate.matchedSignals.includes("postcode");
  const strong = candidate.matchedSignals.length > 1;

  return (
    <li className="rounded-md border bg-background p-3 space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-mono text-sm">{candidate.supplierNo}</span>
        <span className="font-medium">{candidate.cisSupplierName}</span>
        <span className="text-sm text-muted-foreground">
          {candidate.cisDespatchPostcode ?? "no despatch postcode"}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        <Badge
          variant={strong ? "success" : "neutral"}
          className="font-normal mr-2"
        >
          {describeSupplierMatchedSignals(candidate.matchedSignals)}
        </Badge>
        {/* Both postcodes on one line, because "postcode does not match" is
            only actionable next to the two values it is talking about. */}
        {!postcodeAgreed && (
          <span>
            Promo Office has {candidate.promoOfficePostcode ?? "no postcode"},
            One despatches from {candidate.cisDespatchPostcode ?? "no postcode"}
            .
          </span>
        )}
      </p>
      {canDecide && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={busy}
            onClick={onConfirm}
            aria-describedby={error ? errorId : undefined}
          >
            {working ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Confirm
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={onDismiss}
          >
            <X className="h-3.5 w-3.5" />
            Not a match
          </Button>
          {/* Says the recovery before anyone needs it. The suggestion never
              comes back, but linking to this supplier by hand still works, so
              this is not the warning a destructive action would earn. */}
          <span className="text-xs text-muted-foreground">
            Stops this suggestion coming back. You can still link to this
            supplier by hand.
          </span>
        </div>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
    </li>
  );
}

function FindSupplierPanel({
  supplier,
  onLinked,
}: {
  supplier: PromoOfficeUnlinkedSupplier;
  onLinked: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const errorId = useId();
  const [term, setTerm] = useState(supplier.promoOfficeSupplierName);
  const [results, setResults] = useState<PromoOfficeCisSupplier[] | null>(null);
  const [searchFailed, setSearchFailed] = useState<string | null>(null);
  const [linkingNo, setLinkingNo] = useState<string | null>(null);
  // The backend's refusal of the chosen supplier (`supplier_no`): closed,
  // unknown, or the name was linked by somebody else meanwhile.
  const [linkError, setLinkError] = useState<string | null>(null);
  const requestId = useRef(0);

  const trimmed = term.trim();

  useEffect(() => {
    if (trimmed.length === 0) {
      setResults(null);
      setSearchFailed(null);
      return;
    }
    const mine = ++requestId.current;
    const timer = setTimeout(() => {
      setSearchFailed(null);
      dataAdapter
        .searchPromoOfficeCisSuppliers(trimmed)
        .then((found) => {
          // Only the newest search may write. Without this a slow early
          // request can land after a later one and replace good results with
          // stale ones.
          if (mine === requestId.current) setResults(found);
        })
        .catch((err) => {
          if (mine !== requestId.current) return;
          setResults([]);
          setSearchFailed(
            mutationErrorMessage(
              err,
              "One couldn't be searched just now. Try again in a moment.",
            ),
          );
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [dataAdapter, trimmed]);

  const link = async (found: PromoOfficeCisSupplier) => {
    setLinkingNo(found.supplierNo);
    setLinkError(null);
    try {
      await dataAdapter.linkPromoOfficeSupplierToExisting({
        nameKey: supplier.nameKey,
        supplierNo: found.supplierNo,
      });
      toast.success(
        `${supplier.promoOfficeSupplierName} linked to ${found.supplierNo} ${found.name}`,
      );
      onLinked();
    } catch (err) {
      const bound = fieldMessage(err, "supplier_no");
      if (bound) {
        setLinkError(bound);
      } else {
        toast.error(mutationErrorMessage(err, "Could not link this supplier"));
      }
      setLinkingNo(null);
    }
  };

  return (
    <section className="rounded-md border bg-background p-3 space-y-3">
      <div className="space-y-1">
        <Label htmlFor="cis-supplier-search">
          Search One by name or supplier number
        </Label>
        <Input
          id="cis-supplier-search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Supplier name or SUP- number"
          aria-invalid={linkError ? true : undefined}
          aria-describedby={linkError ? errorId : undefined}
        />
        <p className="text-xs text-muted-foreground">
          Searches One&apos;s supplier list directly. Closed suppliers are shown
          but cannot be chosen.
        </p>
      </div>

      {searchFailed && (
        <p className="text-sm text-amber-700 dark:text-amber-400">
          {searchFailed}
        </p>
      )}

      {results !== null && results.length === 0 && !searchFailed && (
        <p className="text-sm text-muted-foreground">
          No supplier in One matches &ldquo;{trimmed}&rdquo;.
        </p>
      )}

      {results !== null && results.length > 0 && (
        <ul className="divide-y rounded-md border">
          {results.map((found) => (
            <li
              key={found.id}
              className="flex flex-wrap items-center justify-between gap-2 p-2"
            >
              <span className="flex flex-wrap items-baseline gap-2">
                <span className="font-mono text-sm">{found.supplierNo}</span>
                <span className="text-sm">{found.name}</span>
                <span className="text-xs text-muted-foreground">
                  {found.despatchPostcode ?? "no despatch postcode"}
                </span>
                {found.isClosed && (
                  <Badge variant="neutral" className="font-normal">
                    Closed
                  </Badge>
                )}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={linkingNo !== null || found.isClosed}
                onClick={() => link(found)}
              >
                {linkingNo === found.supplierNo && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
                Link
              </Button>
            </li>
          ))}
        </ul>
      )}

      {linkError && (
        <p id={errorId} role="alert" className="text-sm text-red-600">
          {linkError}
        </p>
      )}
    </section>
  );
}

/**
 * Ending a link, with why. The reason is required by the backend, not by
 * taste: the purchase orders the link routed stay routed, so the record of
 * why it was wrong has to outlive the link itself.
 */
function UnlinkSupplierDialog({
  supplier,
  link,
  open,
  onOpenChange,
  onUnlinked,
}: {
  supplier: PromoOfficeUnlinkedSupplier;
  link: PromoOfficeSupplierLink;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUnlinked: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const idPrefix = useId();
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  // Re-seed whenever the dialog opens, or a reason typed and abandoned on one
  // supplier would be sitting in the box for the next.
  useEffect(() => {
    if (!open) return;
    setReason("");
    setReasonError(null);
    setFailure(null);
  }, [open]);

  const unlink = async () => {
    setSaving(true);
    setReasonError(null);
    setFailure(null);
    try {
      await dataAdapter.unlinkPromoOfficeSupplier({
        linkId: link.id,
        reason,
      });
      toast.success(
        `${supplier.promoOfficeSupplierName} unlinked from ${link.supplierNo}`,
      );
      onOpenChange(false);
      onUnlinked();
    } catch (err) {
      const fieldErrors = extractFieldErrors(err);
      const forReason = fieldMessage(err, "reason");
      if (forReason) setReasonError(forReason);
      const others = fieldErrors.filter((e) => e.field !== "reason");
      if (others.length > 0) {
        setFailure(others.map((e) => e.message).join(" "));
      } else if (!forReason) {
        setFailure(
          mutationErrorMessage(err, "The link wasn't ended. Try again."),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const reasonId = `${idPrefix}-reason`;
  const reasonErrorId = `${idPrefix}-reason-error`;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!saving) onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Unlink {supplier.promoOfficeSupplierName}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void unlink();
          }}
        >
          <p className="text-sm text-muted-foreground">
            {supplier.promoOfficeSupplierName} will no longer be linked to{" "}
            {link.supplierNo} {link.cisSupplierName}. Purchase orders already
            matched stay matched and released lines keep their supplier; new
            work naming {supplier.promoOfficeSupplierName} waits until someone
            links the name again.
          </p>
          {failure && (
            <p
              role="alert"
              className="rounded-md border border-red-300 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:text-red-400"
            >
              {failure}
            </p>
          )}
          <div className="space-y-1">
            <Label htmlFor={reasonId}>Why is the link wrong?</Label>
            <Textarea
              id={reasonId}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Different company trading under a similar name"
              disabled={saving}
              aria-invalid={reasonError ? true : undefined}
              aria-describedby={reasonError ? reasonErrorId : undefined}
            />
            {reasonError && (
              <p id={reasonErrorId} className="text-sm text-red-600">
                {reasonError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Unlink
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
